import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flame, Heart, Users, Globe, Sparkles, ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { setPageSeo, resetPageSeo, SEO_BASE } from "@/lib/seo";

const LANGS = ["fr", "en", "es", "es_mx", "de", "it", "pl", "fil", "ru", "el", "hi", "ja", "zh"];

export default function Toussaint() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [openFaq, setOpenFaq] = useState(0);

  const faq = t("tss.faq", { returnObjects: true }) || [];
  const faqArr = Array.isArray(faq) ? faq : [];

  useEffect(() => {
    const url = `${SEO_BASE}/toussaint?lang=${lang}`;
    const img = `${SEO_BASE}/toussaint-og.jpg`;
    const alternates = LANGS.map((l) => ({ lang: l, href: `${SEO_BASE}/toussaint?lang=${l}` }));
    setPageSeo({
      title: t("tss.metaTitle"),
      description: t("tss.metaDesc"),
      canonicalPath: url,
      image: img,
      ogType: "website",
      alternates,
      xDefault: `${SEO_BASE}/toussaint`,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": t("tss.metaTitle"),
          "description": t("tss.metaDesc"),
          "inLanguage": lang.replace("_", "-"),
          "url": url,
          "primaryImageOfPage": img,
          "isPartOf": {
            "@type": "WebSite",
            "name": "BougieDuCœur",
            "url": `${SEO_BASE}/`,
          },
          "about": { "@type": "Thing", "name": t("tss.metaTitle") },
        },
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "BougieDuCœur",
          "url": `${SEO_BASE}/`,
          "logo": `${SEO_BASE}/logo.png`,
          "description": t("tss.worldFirst"),
        },
        ...(faqArr.length ? [{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqArr.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }] : []),
      ],
    });
    return () => resetPageSeo();
  }, [lang, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const goCreate = () => navigate("/creer");

  const sections = [
    { icon: Flame, t: t("tss.s1t"), p: t("tss.s1p") },
    { icon: Users, t: t("tss.s2t"), p: t("tss.s2p") },
    { icon: Globe, t: t("tss.s3t"), p: t("tss.s3p") },
  ];

  return (
    <div className="min-h-[100dvh] bg-black text-white" data-testid="toussaint-page">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center justify-between bg-black/90 px-5 py-4 backdrop-blur" style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}>
        <button onClick={() => navigate("/")} data-testid="toussaint-logo" aria-label="BougieDuCœur">
          <Logo />
        </button>
        <LanguageSwitcher />
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/toussaint-hero.jpg" alt="" aria-hidden="true" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
        </div>
        <div className="relative mx-auto max-w-3xl px-6 py-16 text-center sm:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-amber-200" data-testid="toussaint-worldfirst">
            <Sparkles size={13} /> {t("tss.worldFirst")}
          </span>
          <h1 className="mt-6 text-3xl font-semibold leading-tight tracking-tight sm:text-5xl" data-testid="toussaint-h1">
            {t("tss.h1")}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg" data-testid="toussaint-intro">
            {t("tss.intro")}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button onClick={goCreate} data-testid="toussaint-cta-hero" className="h-14 w-full rounded-full bg-primary px-8 text-base font-semibold shadow-[0_4px_24px_0_rgba(56,189,248,0.45)] transition-transform hover:scale-[1.02] sm:w-auto">
              <Flame size={18} className="mr-2" /> {t("tss.ctaBtn")}
            </Button>
            <button onClick={() => navigate("/blog")} data-testid="toussaint-blog-link" className="text-sm text-white/60 underline-offset-4 hover:text-white hover:underline">
              {t("tss.blogBtn")}
            </button>
          </div>
        </div>
      </section>

      {/* Offer */}
      <section className="mx-auto max-w-3xl px-6 -mt-4 sm:-mt-8" data-testid="toussaint-offer">
        <div className="relative overflow-hidden rounded-3xl border border-amber-300/30 bg-gradient-to-br from-amber-300/[0.08] to-amber-500/[0.03] p-7 text-center sm:p-9">
          <span className="inline-block rounded-full bg-amber-300 px-3 py-1 text-xs font-bold uppercase tracking-wide text-black" data-testid="toussaint-offer-badge">
            {t("tss.offerBadge")} · {t("tss.offerPrice")}
          </span>
          <h2 className="mt-4 text-2xl font-semibold text-amber-100 sm:text-3xl" data-testid="toussaint-offer-title">{t("tss.offerTitle")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base" data-testid="toussaint-offer-text">{t("tss.offerText")}</p>
          <Button onClick={goCreate} data-testid="toussaint-offer-cta" className="mt-6 h-12 rounded-full bg-amber-300 px-8 text-base font-semibold text-black transition-transform hover:scale-[1.02] hover:bg-amber-200">
            {t("tss.offerBtn")} <ArrowRight size={17} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Content sections */}
      <section className="mx-auto max-w-3xl px-6 py-14 sm:py-20">
        <div className="space-y-10">
          {sections.map((s, i) => {
            const Icon = s.icon;
            return (
              <article key={i} className="flex gap-4 sm:gap-5" data-testid={`toussaint-section-${i}`}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-sky-400/30 bg-sky-400/10 text-sky-300">
                  <Icon size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-white sm:text-xl">{s.t}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-white/65 sm:text-base">{s.p}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      {faqArr.length > 0 && (
        <section className="mx-auto max-w-3xl px-6 pb-16" data-testid="toussaint-faq">
          <h2 className="mb-6 text-center text-xl font-semibold sm:text-2xl">{t("tss.faqTitle")}</h2>
          <div className="space-y-3">
            {faqArr.map((f, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-sm font-medium text-white/90 hover:bg-white/[0.03] sm:text-base"
                  data-testid={`toussaint-faq-q-${i}`}
                >
                  {f.q}
                  <ChevronDown size={18} className={`shrink-0 text-white/50 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <p className="px-5 pb-4 text-sm leading-relaxed text-white/60" data-testid={`toussaint-faq-a-${i}`}>{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="border-t border-white/10 px-6 py-16 text-center" data-testid="toussaint-final-cta">
        <Heart size={26} className="mx-auto text-amber-300" />
        <h2 className="mt-4 text-2xl font-semibold sm:text-3xl">{t("tss.ctaTitle")}</h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-white/65 sm:text-base">{t("tss.ctaText")}</p>
        <Button onClick={goCreate} data-testid="toussaint-cta-final" className="mt-7 h-14 rounded-full bg-primary px-10 text-base font-semibold shadow-[0_4px_24px_0_rgba(56,189,248,0.45)] transition-transform hover:scale-[1.02]">
          <Flame size={18} className="mr-2" /> {t("tss.ctaBtn")}
        </Button>
      </section>
    </div>
  );
}
